import {
  ApolloClient,
  createHttpLink,
  from,
  InMemoryCache,
  type NormalizedCacheObject
} from "@apollo/client"
import { setContext } from "@apollo/client/link/context"
import { onError } from "@apollo/client/link/error"
import { RetryLink } from "@apollo/client/link/retry"
import {
  createApolloOperationLink,
  handleApolloOperationError,
  registerApolloClient
} from "@pathwright/utils/src/graphql/apolloOperation"
import { createConnectionGateLink } from "@pathwright/utils/src/graphql/connectionGate"
import { getApolloClientVersion } from "@pathwright/utils/src/client-build/clientBuildMeta"

const BLOCKS_GRAPHQL_PATH = "/blocks/graphql"

export type BlocksApolloClientOptions = {
  endpoint: string
  authToken?: string | null
  headers?: Record<string, string>
}

// The consumer owns endpoint selection, authentication, and client lifetime.
export const getApolloClient = ({
  endpoint,
  authToken = null,
  headers = {}
}: BlocksApolloClientOptions): ApolloClient<NormalizedCacheObject> => {
  if (!endpoint)
    throw new Error("Blocks requires an explicit GraphQL endpoint or client")

  // https://www.apollographql.com/docs/link/links/retry
  // Default is 5 attempts
  const retryLink = new RetryLink()

  const middlewareLink = setContext((req, context) => ({
    headers: {
      ...headers,
      ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      ...context.headers
    }
  }))

  const httpLink = createHttpLink({
    uri: (operation) => {
      let uri = endpoint

      // Supply an operationName as a convenience for identifying the operation
      // via the endpoint, which, for instance, is all that is included in
      // Sentry breadcrumbs. So, this can help with debugging.
      if (operation.operationName) {
        uri += `${
          uri.includes("?") ? "&" : "?"
        }operationName=${encodeURIComponent(operation.operationName)}`
      }

      return uri
    }
  })

  const operationLink = createApolloOperationLink(BLOCKS_GRAPHQL_PATH)

  const errorLink = onError((options) => {
    const { graphQLErrors } = options

    handleApolloOperationError(options, { graphqlPath: BLOCKS_GRAPHQL_PATH })

    if (graphQLErrors)
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        )
      )
  })

  const connectionGateLink = createConnectionGateLink(endpoint)

  const clientVersion = getApolloClientVersion()
  const client = new ApolloClient({
    link: from([
      operationLink,
      retryLink,
      middlewareLink,
      errorLink,
      connectionGateLink,
      httpLink
    ]),
    name: "blocks",
    ...(clientVersion ? { version: clientVersion } : {}),
    cache: new InMemoryCache({
      typePolicies: {
        Query: {
          fields: {
            mediaByAccount: {
              keyArgs: ["contextKey"], // Ensures cache keys are unique per context
              merge(existing = [], incoming = []) {
                return incoming // Full replacement is fine since our logic handles it
              }
            },
            contentList: {
              keyArgs: ["mode", "draft", "contextKeys", "userID"], // Scope the cache by 'mode'
              merge(existing, incoming) {
                return incoming // Replace cache instead of merging
              }
            },
            muxVideoEmbed: {
              keyArgs: ["id"]
            }
          }
        },
        MuxVideoEmbed: {
          keyFields: ["id"]
        },
        Content: {
          fields: {
            blocks: {
              merge(existing, incoming) {
                return incoming
              },
              keyArgs: (_, { variables }) => {
                const { mode, userID } = variables as {
                  mode?: unknown
                  userID?: unknown
                }
                return `${mode}:${userID}`
              }
            }
          }
        }
      }
    })
  })

  registerApolloClient(BLOCKS_GRAPHQL_PATH, client)
  return client
}

export default getApolloClient
